/**
 * Orders / Receiving tab — POs grouped by vendor, line items, receiving.
 * 17.4: POs, statuses, lines, send PO, request status, receive per line.
 */

import { useCallback, useEffect, useState } from 'react';
import { format, parseISO, differenceInDays } from 'date-fns';
import {
  fetchPurchaseOrders, sendPo, requestStatus, receiveLine, updatePoLine,
} from '../../../api/purchasing';
import {
  fetchEmailMessage, fetchEmailAttachments, openAttachment,
} from '../../../api/rfq';
import { Button, LoadingSpinner, EmptyState, Modal } from '../../common';
import UploadPoModal from './UploadPoModal';
import ReceiveAllModal from './ReceiveAllModal';
import { openFileInTab } from '../../../api/files';

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
}

// V2-b R2 — provenance labels for the inline editor tooltip.
const DELIVERY_SOURCE_LABEL = {
  quote_seed: 'quote',
  manual: 'manual',
  vendor_email: 'vendor email',
  vendor_confirmation: 'vendor confirmation',
  backfill: 'backfill',
};

function provenanceCaption(source, updatedAt) {
  if (!source) return '';
  const label = DELIVERY_SOURCE_LABEL[source] || source;
  if (!updatedAt) return label;
  try {
    return `${label} ${format(parseISO(updatedAt), 'M/d')}`;
  } catch {
    return label;
  }
}

/**
 * V2-b R2 — inline delivery-date editor.
 *
 * Renders the current date as a click-to-edit affordance. On click a
 * <input type="date"> takes over; blur / Enter saves via PATCH; Escape
 * cancels. Clearing the input to empty is a valid PATCH → null (Kevin
 * can un-set a date). The `provenance` line renders as small dim text
 * beneath the date so Kevin sees who last touched it.
 *
 * The parent's refresh callback runs on success so downstream columns
 * (LATE badge, provenance tooltip, projected dates elsewhere) pick up
 * the change.
 */
function EditableDeliveryDate({
  lineId, field, currentValue, source, updatedAt, canEdit,
  onSaved,
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentValue || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setValue(currentValue || ''); }, [currentValue]);

  const save = useCallback(async () => {
    setError('');
    setSaving(true);
    try {
      // Empty string → null (Kevin can clear a date). ISO yyyy-mm-dd
      // is what <input type="date"> emits; DRF's DateField parses it.
      const payload = { [field]: value === '' ? null : value };
      await updatePoLine(lineId, payload);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [lineId, field, value, onSaved]);

  const cancel = useCallback(() => {
    setValue(currentValue || '');
    setEditing(false);
    setError('');
  }, [currentValue]);

  if (!canEdit) {
    return (
      <div>
        <div className="text-gray-500">{fmtDate(currentValue)}</div>
        {source && (
          <div className="text-[10px] text-gray-400 mt-0.5">
            {provenanceCaption(source, updatedAt)}
          </div>
        )}
      </div>
    );
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          disabled={saving}
          autoFocus
          className="border border-gray-300 rounded px-1 py-0.5 text-xs"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="text-green-600 hover:text-green-800 text-xs px-1"
          title="Save"
        >
          ✓
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="text-gray-500 hover:text-gray-700 text-xs px-1"
          title="Cancel"
        >
          ✕
        </button>
        {error && <span className="text-red-600 text-[10px] ml-1">{error}</span>}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-left group"
      title={source ? `Last set: ${provenanceCaption(source, updatedAt)}` : 'Click to edit'}
    >
      <div className="text-gray-700 group-hover:text-blue-600 group-hover:underline">
        {fmtDate(currentValue)}
      </div>
      {source && (
        <div className="text-[10px] text-gray-400 mt-0.5">
          {provenanceCaption(source, updatedAt)}
        </div>
      )}
    </button>
  );
}

function fmtCurrency(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

const PO_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  po_uploaded: 'bg-blue-100 text-blue-700',
  po_sent: 'bg-indigo-100 text-indigo-700',
  confirmed: 'bg-teal-100 text-teal-700',
  partial_confirmed: 'bg-teal-100 text-teal-700',
  partial_received: 'bg-yellow-100 text-yellow-700',
  received: 'bg-green-100 text-green-700',
  closed: 'bg-gray-200 text-gray-500',
  cancelled: 'bg-red-100 text-red-700',
};

// Human-readable status labels
const PO_STATUS_LABELS = {
  draft: 'Draft',
  po_uploaded: 'Ordered',
  po_sent: 'Sent to Vendor',
  confirmed: 'Confirmed',
  partial_confirmed: 'Partial Confirmed',
  partial_received: 'Partial Received',
  received: 'Received',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export default function OrdersTab({ job, onRefresh }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [receiveModal, setReceiveModal] = useState(null);
  const [receiveQty, setReceiveQty] = useState('');

  // Email viewer for PO link
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [openingAttachment, setOpeningAttachment] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const data = await fetchPurchaseOrders({ job: job.id });
      setOrders(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleSendPo(poId) {
    setActionLoading(`send-${poId}`);
    try {
      await sendPo(poId);
      loadData();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send PO');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRequestStatus(poId) {
    setActionLoading(`status-${poId}`);
    try {
      await requestStatus(poId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to request status');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleViewPoEmail(gmailMessageId) {
    setEmailData(null);
    setEmailAttachments([]);
    setEmailModalOpen(true);
    setEmailLoading(true);
    try {
      // po.inbound_email_id is the DB UUID of the EmailMessage record.
      // GET /email/{id}/ looks up by primary key.
      const msg = await fetchEmailMessage(gmailMessageId);
      setEmailData(msg);
      try {
        const atts = await fetchEmailAttachments(msg.id);
        setEmailAttachments(atts);
      } catch {
        setEmailAttachments([]);
      }
    } catch {
      setEmailData({ error: true });
    } finally {
      setEmailLoading(false);
    }
  }

  async function handleOpenAttachment(emailId, attId, filename) {
    setOpeningAttachment(attId);
    try {
      await openAttachment(emailId, attId, filename);
    } catch {
      alert('Could not open attachment. It may no longer be available in Gmail.');
    } finally {
      setOpeningAttachment(null);
    }
  }

  async function handleReceive() {
    if (!receiveModal) return;
    const qty = parseInt(receiveQty);
    if (!qty || qty <= 0) { alert('Enter a valid quantity'); return; }
    setActionLoading('receive');
    try {
      await receiveLine(receiveModal.id, { qty_received: qty });
      setReceiveModal(null);
      setReceiveQty('');
      loadData();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Receive failed');
    } finally {
      setActionLoading(null);
    }
  }

  // Upload PO modal
  const [uploadPoOpen, setUploadPoOpen] = useState(false);

  // Receive All modal
  const [receiveAllPo, setReceiveAllPo] = useState(null);

  if (loading) {
    return <div className="flex justify-center py-10"><LoadingSpinner /></div>;
  }

  const canEdit = !['complete', 'archived'].includes(job.status);

  return (
    <div className="space-y-3">
      {/* Upload PO button + header */}
      {canEdit && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">{orders.length} purchase order{orders.length !== 1 ? 's' : ''}</p>
          <Button size="sm" onClick={() => setUploadPoOpen(true)}>
            📄 Upload PO
          </Button>
        </div>
      )}

      {orders.length === 0 && (
        <EmptyState
          title="No purchase orders"
          description="Upload a TrulinX PO or confirm quote selections to create orders."
        />
      )}

      <UploadPoModal
        open={uploadPoOpen}
        onClose={() => setUploadPoOpen(false)}
        job={job}
        onUploaded={() => { loadData(); onRefresh?.(); }}
      />
      {orders.map((po) => {
        const lines = po.lines || [];
        const statusColor = PO_STATUS_COLORS[po.order_status] || 'bg-gray-100 text-gray-600';

        return (
          <div key={po.id} className="bg-white rounded-lg border border-gray-200">
            {/* PO header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                {po.inbound_email_id ? (
                  <button
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    onClick={(e) => { e.stopPropagation(); handleViewPoEmail(po.inbound_email_id); }}
                    title="View original PO email"
                  >
                    PO #{po.po_number || po.id.slice(0, 8)}
                  </button>
                ) : (
                  <span className="font-medium text-foreground">
                    PO #{po.po_number || po.id.slice(0, 8)}
                  </span>
                )}
                <span className="text-sm text-gray-500">
                  {po.vendor_name || po.vendor || '—'}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusColor}`}>
                  {PO_STATUS_LABELS[po.order_status] || po.order_status}
                </span>
                <span className="text-xs text-gray-400">
                  {fmtDate(po.created_at)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {/* PDF link */}
                {po.po_pdf_file && (
                  <button
                    onClick={() => openFileInTab(po.po_pdf_file).catch(() => alert('Could not open PDF.'))}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                    title="View PO PDF"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    PDF
                  </button>
                )}
                {/* Receive All */}
                {canEdit && lines.some(l => (l.qty_received || 0) < (l.qty_ordered || 0) && l.line_status !== 'cancelled') && (
                  <Button size="sm" variant="secondary" onClick={() => setReceiveAllPo(po)}>
                    Receive All
                  </Button>
                )}
                {canEdit && po.order_status === 'draft' && (
                  <Button size="sm" variant="secondary" loading={actionLoading === `send-${po.id}`} onClick={() => handleSendPo(po.id)}>
                    Send PO
                  </Button>
                )}
                {canEdit && ['sent', 'acknowledged', 'partial', 'po_sent', 'confirmed', 'partial_received', 'po_uploaded'].includes(po.order_status) && (
                  <Button size="sm" variant="ghost" loading={actionLoading === `status-${po.id}`} onClick={() => handleRequestStatus(po.id)}>
                    Request Status
                  </Button>
                )}
              </div>
            </div>

            {/* PO lines */}
            {lines.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-3 py-2">Part</th>
                      <th className="px-3 py-2 text-right">Qty Ordered</th>
                      <th className="px-3 py-2 text-right">Qty Received</th>
                      <th className="px-3 py-2 text-right">Unit Price</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2">Expected</th>
                      <th className="px-3 py-2 text-center">Status</th>
                      {canEdit && <th className="px-3 py-2 w-20" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lines.map((line) => {
                      const isLate = line.expected_delivery_date &&
                        differenceInDays(new Date(), parseISO(line.expected_delivery_date)) > 0 &&
                        (line.qty_received || 0) < (line.qty_ordered || 0);

                      return (
                        <tr key={line.id} className={`hover:bg-gray-50 ${isLate ? 'bg-red-50/50' : ''}`}>
                          <td className="px-3 py-2 font-medium">{line.part_display || line.part || '—'}</td>
                          <td className="px-3 py-2 text-right">{line.qty_ordered}</td>
                          <td className="px-3 py-2 text-right">{line.qty_received || 0}</td>
                          <td className="px-3 py-2 text-right">{fmtCurrency(line.unit_price)}</td>
                          <td className="px-3 py-2 text-right">{fmtCurrency(line.total_price || (line.unit_price * line.qty_ordered))}</td>
                          <td className="px-3 py-2">
                            {/* V2-b R2 — inline editor. LATE badge
                                stays alongside the date; provenance
                                caption appears beneath. */}
                            <div className="flex items-start gap-1">
                              <EditableDeliveryDate
                                lineId={line.id}
                                field="expected_delivery_date"
                                currentValue={line.expected_delivery_date}
                                source={line.expected_delivery_source}
                                updatedAt={line.expected_delivery_updated_at}
                                canEdit={canEdit}
                                onSaved={() => { loadData(); onRefresh?.(); }}
                              />
                              {isLate && (
                                <span className="text-red-600 text-xs font-medium">
                                  LATE
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              (line.qty_received || 0) >= (line.qty_ordered || 1) ? 'bg-green-100 text-green-700' :
                              (line.qty_received || 0) > 0 ? 'bg-yellow-100 text-yellow-700' :
                              po.order_status === 'confirmed' ? 'bg-teal-100 text-teal-700' :
                              ['po_sent', 'po_uploaded'].includes(po.order_status) ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {(line.qty_received || 0) >= (line.qty_ordered || 1) ? 'Received' :
                               (line.qty_received || 0) > 0 ? 'Partial' :
                               po.order_status === 'confirmed' ? 'Confirmed' :
                               po.order_status === 'po_sent' ? 'On Order' :
                               po.order_status === 'po_uploaded' ? 'Ordered' : 'Pending'}
                            </span>
                          </td>
                          {canEdit && (
                            <td className="px-3 py-2">
                              {(line.qty_received || 0) < (line.qty_ordered || 0) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => { setReceiveModal(line); setReceiveQty(''); }}
                                >
                                  Receive
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* PO Email Viewer modal */}
      <Modal
        open={emailModalOpen}
        onClose={() => { setEmailModalOpen(false); setEmailData(null); setEmailAttachments([]); }}
        title="Original PO Email"
        maxWidth="max-w-2xl"
      >
        {emailLoading && <div className="flex justify-center py-8"><LoadingSpinner /></div>}
        {!emailLoading && emailData?.error && (
          <p className="text-sm text-red-500 py-4">Could not load email. It may have been deleted or is unavailable.</p>
        )}
        {!emailLoading && emailData && !emailData.error && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm bg-gray-50 rounded-md p-3 border border-gray-200">
              <div>
                <span className="text-xs text-gray-400 uppercase font-medium block mb-0.5">From</span>
                <span className="text-gray-800 font-medium">{emailData.sender || '—'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-400 uppercase font-medium block mb-0.5">Received</span>
                <span className="text-gray-800">{emailData.received_at ? fmtDate(emailData.received_at) : '—'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-xs text-gray-400 uppercase font-medium block mb-0.5">Subject</span>
                <span className="text-gray-800">{emailData.subject || '(no subject)'}</span>
              </div>
            </div>

            {emailAttachments.length > 0 && (
              <div>
                <span className="text-xs text-gray-400 uppercase font-medium block mb-1.5">Attachments ({emailAttachments.length})</span>
                <div className="flex flex-wrap gap-2">
                  {emailAttachments.map((att) => {
                    const isLoading = openingAttachment === att.id;
                    const sizeKb = att.size_bytes ? `${Math.round(att.size_bytes / 1024)} KB` : null;
                    return (
                      <button
                        key={att.id}
                        disabled={isLoading}
                        onClick={() => handleOpenAttachment(emailData.id, att.id, att.filename)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50 hover:border-blue-400 text-sm text-gray-700 transition-colors disabled:opacity-60 shadow-sm"
                        title={att.is_pdf ? 'Open PDF in new tab' : `Download ${att.filename}`}
                      >
                        {isLoading ? (
                          <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        )}
                        <span className="truncate max-w-[180px]">{att.filename}</span>
                        {sizeKb && <span className="text-xs text-gray-400 flex-shrink-0">{sizeKb}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <span className="text-xs text-gray-400 uppercase font-medium block mb-1">Email Body</span>
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-white border border-gray-200 rounded-md p-3 max-h-80 overflow-y-auto font-sans leading-relaxed">
                {emailData.body_text || '(empty body)'}
              </pre>
            </div>

            <div className="flex justify-end pt-1">
              <Button variant="ghost" size="sm" onClick={() => { setEmailModalOpen(false); setEmailData(null); setEmailAttachments([]); }}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Receive modal */}
      <Modal
        open={!!receiveModal}
        onClose={() => setReceiveModal(null)}
        title="Receive Parts"
      >
        {receiveModal && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Receiving for: <strong>{receiveModal.part_display || receiveModal.part}</strong>
            </p>
            <p className="text-sm text-gray-500">
              Ordered: {receiveModal.qty_ordered} &middot; Already received: {receiveModal.qty_received || 0}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity to receive</label>
              <input
                type="number"
                min="1"
                max={(receiveModal.qty_ordered || 0) - (receiveModal.qty_received || 0)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={receiveQty}
                onChange={(e) => setReceiveQty(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setReceiveModal(null)}>Cancel</Button>
              <Button size="sm" loading={actionLoading === 'receive'} onClick={handleReceive}>Receive</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Receive All modal */}
      <ReceiveAllModal
        open={!!receiveAllPo}
        onClose={() => setReceiveAllPo(null)}
        po={receiveAllPo}
        onReceived={() => { loadData(); onRefresh?.(); }}
      />
    </div>
  );
}
